import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays, startOfMonth, endOfMonth } from 'date-fns';
import { useCurrentAppUser } from './useCurrentAppUser';
import { getDealSalesAmount } from '@/utils/salesAmount';

export interface DealWithContactAge {
  id: string;
  name: string | null;
  stage: string;
  total_amount: number | null;
  contact_id: string | null;
  created_at: string;
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  last_date: Date | null;
  days_since: number;
  sdr_name: string | null;
}

export interface ConsultantContactStats {
  dealsByLastContact: DealWithContactAge[];   // any call or message
  dealsByLastAttempt: DealWithContactAge[];   // any call (regardless of duration)
  dealsByLastEffective: DealWithContactAge[]; // calls > 60s only
  isLoading: boolean;
}

export interface ConsultantSalesStats {
  soldAmount: number;
  soldCount: number;
  goalAmount: number;
  receivedAmount: number;
  isLoading: boolean;
}

// Returns three sorted deal lists based on contact recency
export function useConsultantContactStats(): ConsultantContactStats {
  const { data: currentUser } = useCurrentAppUser();
  const consultantId = currentUser?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['consultant-contact-stats', consultantId],
    enabled: !!consultantId,
    staleTime: 3 * 60 * 1000,
    queryFn: async () => {
      // 1. Fetch active deals for this consultant
      const { data: deals, error: dealsError } = await supabase
        .from('deals')
        .select(`
          id, name, stage, total_amount, contact_id, created_at, sales_development,
          contact:contacts!fk_deals_contact(id, first_name, last_name)
        `)
        .eq('sales_consultant', consultantId!)
        .not('stage', 'in', '("won","lost")')
        .order('created_at', { ascending: false });

      if (dealsError) throw dealsError;
      if (!deals || deals.length === 0) {
        return { dealsByLastContact: [], dealsByLastAttempt: [], dealsByLastEffective: [] };
      }

      const contactIds = [...new Set(deals.map((d) => d.contact_id).filter(Boolean) as string[])];

      // 2. Fetch calls in parallel: all attempts + effective (>60s)
      const [callsRes, effectiveRes, messagesRes] = await Promise.all([
        // Any call (not just canceled scheduled ones)
        supabase
          .from('calls')
          .select('contact_id, started_at')
          .in('contact_id', contactIds)
          .neq('status', 'canceled')
          .order('started_at', { ascending: false }),

        // Effective calls: duration > 60s
        supabase
          .from('calls')
          .select('contact_id, started_at')
          .in('contact_id', contactIds)
          .gt('duration_seconds', 60)
          .order('started_at', { ascending: false }),

        // Messages (WhatsApp, etc.) — for "last contact" calculation
        supabase
          .from('messages')
          .select('contact_id, created_at')
          .in('contact_id', contactIds)
          .order('created_at', { ascending: false }),
      ]);

      // Build maps: contactId → most recent date
      const attemptMap = new Map<string, Date>();
      (callsRes.data ?? []).forEach((c) => {
        if (c.contact_id && c.started_at && !attemptMap.has(c.contact_id)) {
          attemptMap.set(c.contact_id, new Date(c.started_at));
        }
      });

      const effectiveMap = new Map<string, Date>();
      (effectiveRes.data ?? []).forEach((c) => {
        if (c.contact_id && c.started_at && !effectiveMap.has(c.contact_id)) {
          effectiveMap.set(c.contact_id, new Date(c.started_at));
        }
      });

      // "Any contact" = max(last attempt, last message)
      const contactMap = new Map<string, Date>(attemptMap);
      (messagesRes.data ?? []).forEach((m) => {
        if (!m.contact_id || !m.created_at) return;
        const msgDate = new Date(m.created_at);
        const existing = contactMap.get(m.contact_id);
        if (!existing || msgDate > existing) {
          contactMap.set(m.contact_id, msgDate);
        }
      });

      const now = new Date();

      // Fetch SDR names
      const sdrIds = [...new Set(deals.map((d: any) => d.sales_development).filter(Boolean) as string[])];
      const sdrMap = new Map<string, string>();
      if (sdrIds.length > 0) {
        const { data: sdrUsers } = await supabase
          .from('app_users')
          .select('id, first_name, last_name')
          .in('id', sdrIds);
        (sdrUsers ?? []).forEach((u) => {
          sdrMap.set(u.id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '—');
        });
      }

      const enrich = (map: Map<string, Date>) =>
        deals
          .map((deal): DealWithContactAge => {
            const cid = deal.contact_id;
            const lastDate = cid ? (map.get(cid) ?? null) : null;
            const fallback = new Date(deal.created_at);
            const refDate = lastDate ?? fallback;
            const sdrId = (deal as any).sales_development;
            return {
              ...deal,
              contact: Array.isArray(deal.contact) ? deal.contact[0] : deal.contact,
              last_date: lastDate,
              days_since: differenceInDays(now, refDate),
              sdr_name: sdrId ? (sdrMap.get(sdrId) ?? null) : null,
            };
          })
          .sort((a, b) => b.days_since - a.days_since);

      return {
        dealsByLastContact: enrich(contactMap),
        dealsByLastAttempt: enrich(attemptMap),
        dealsByLastEffective: enrich(effectiveMap),
      };
    },
  });

  return {
    dealsByLastContact: data?.dealsByLastContact ?? [],
    dealsByLastAttempt: data?.dealsByLastAttempt ?? [],
    dealsByLastEffective: data?.dealsByLastEffective ?? [],
    isLoading,
  };
}

// Returns sold amount/count and goal for a given month
export function useConsultantSalesStats(year: number, month: number): ConsultantSalesStats {
  const { data: currentUser } = useCurrentAppUser();
  const consultantId = currentUser?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['consultant-sales-stats', consultantId, year, month],
    enabled: !!consultantId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const periodStart = startOfMonth(new Date(year, month - 1)).toISOString();
      const periodEnd = endOfMonth(new Date(year, month - 1)).toISOString();

      const [soldRes, goalRes] = await Promise.all([
        // Won deals closed this month
        supabase
          .from('deals')
          .select('id, total_amount, upsell_credit_amount, deal_status, cancelled_at, closed_date')
          .eq('sales_consultant', consultantId!)
          .eq('stage', 'won')
          .not('closed_date', 'is', null)
          .gte('closed_date', periodStart)
          .lte('closed_date', periodEnd),

        // Goals for this user/month (sum all BUs)
        supabase
          .from('user_sales_goals')
          .select('goal_amount')
          .eq('user_id', consultantId!)
          .eq('year', year)
          .eq('month', month)
          .not('business_unit', 'is', null),
      ]);

      // Filter and calculate using the same logic as admin dashboard
      const validDeals = (soldRes.data ?? []).filter(d => {
        // Exclude deals cancelled in the same month as closed
        if (d.deal_status === 'cancelled' && d.cancelled_at && d.closed_date) {
          const closedYM = d.closed_date.slice(0, 7);
          const cancelledYM = d.cancelled_at.slice(0, 7);
          if (closedYM === cancelledYM) return false;
        }
        return true;
      });

      const soldAmount = validDeals.reduce(
        (sum, d) => sum + getDealSalesAmount({
          deal_status: d.deal_status,
          closed_date: d.closed_date,
          cancelled_at: d.cancelled_at,
          total_amount: d.total_amount,
          upsell_credit_amount: d.upsell_credit_amount,
        }),
        0
      );
      const soldCount = validDeals.length;
      const goalAmount = (goalRes.data ?? []).reduce(
        (sum, g) => sum + (Number(g.goal_amount) || 0),
        0
      );

      // Recebido: payments paid this month on this consultant's deals
      // Uses inner join to avoid URL length limits with large deal counts
      const { data: paymentsRes } = await supabase
        .from('payments')
        .select('paid_amount, deal:deals!inner(sales_consultant)')
        .eq('deal.sales_consultant', consultantId!)
        .eq('status', 'paid')
        .gte('paid_at', periodStart)
        .lte('paid_at', periodEnd);

      const receivedAmount = (paymentsRes ?? []).reduce(
        (sum, p) => sum + (Number(p.paid_amount) || 0),
        0
      );

      return { soldAmount, soldCount, goalAmount, receivedAmount };
    },
  });

  return {
    soldAmount: data?.soldAmount ?? 0,
    soldCount: data?.soldCount ?? 0,
    goalAmount: data?.goalAmount ?? 0,
    receivedAmount: data?.receivedAmount ?? 0,
    isLoading,
  };
}
